import { useContext } from 'react';
import { ReactReduxContext } from 'react-redux';
import { useAppSelector } from '../../redux/hooks';
import { mapSavedSearchesToRecent } from './map-saved-searches-to-recent';
import { savedSearchLocale } from '../saved-searches/edit-saved-search-url';
import { useLocaleMessagesFor } from '../../i18n/use-locale-messages';
import type { SearchPanelRecentSearch } from '../../components/search-panel/types';

/**
 * Search panel spec step 4: SearchPanel's `recentSearches` prop, sourced from Redux
 * `savedSearches` when a store is mounted above the caller, or an empty list when it isn't (the
 * MinimalChrome pages FallbackLauncher/MobileBottomNav can render on have no Redux Provider
 * either, same as they have no SearchProvider). Same guard useValueSheetLedger.ts already uses:
 * `useContext(ReactReduxContext) != null` reads whether a store exists without throwing the way
 * `useAppSelector` would with no Provider, and it's safe to branch which hook runs on because
 * that answer is fixed for a given render tree's lifetime, it never flips after mount.
 */
export function useRecentSearchesForPanel(limit = 2): SearchPanelRecentSearch[] {
  const hasReduxStore = useContext(ReactReduxContext) != null;
  // eslint-disable-next-line react-hooks/rules-of-hooks -- stable for the component's lifetime, see comment above (matches useValueSheetLedger.ts)
  return hasReduxStore ? useRecentSearchesFromStore(limit) : [];
}

function useRecentSearchesFromStore(limit: number): SearchPanelRecentSearch[] {
  const entries = useAppSelector((state) => state.recentSearches?.savedSearches ?? []);
  const recent = mapSavedSearchesToRecent(entries, limit);
  // Filter-only labels are built in each search's own locale; this re-renders once it's loaded.
  useLocaleMessagesFor(recent.map((r) => savedSearchLocale(r.url)));
  return recent;
}
